import axios from 'axios';
import { useAuthStore } from '@/store/authStore';
import { env } from '@/config/env';
import { trackSessionExpired } from '@/lib/analytics/events';

const apiClient = axios.create({
  baseURL: `${env.apiUrl}/${env.apiVersion}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const excludedPaths = ['/auth/login', '/auth/register', '/auth/refresh'];

    // Handle 401 errors (e.g., token expired), but exclude specific auth paths
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !excludedPaths.some((path) => originalRequest.url?.includes(path))
    ) {
      originalRequest._retry = true;

      try {
        const { refreshToken } = useAuthStore.getState();

        if (!refreshToken) {
          trackSessionExpired('no_refresh_token');
          useAuthStore.getState().clearAuth();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Attempt to refresh token using the new backend format
        const { data } = await axios.post(`${env.apiUrl}/${env.apiVersion}/auth/refresh`, {
          refresh_token: refreshToken, // snake_case
        });

        // Update auth store with new tokens (backend returns access_token, refresh_token)
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          useAuthStore.getState().setAuth(currentUser, data.access_token, data.refresh_token);
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        trackSessionExpired('token_refresh_failed');
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient };
